/* facets.js (FULL FILE)
   ✅ Mobile-only fix: build searchParams from real DOM state (FormData) on mobile so deselect stays deselected.
   ✅ Desktop logic remains EXACTLY as before (keeps checkboxStates Map behavior).
*/

class FacetFiltersForm extends HTMLElement {
  static get observedAttributes() {
    return ['selected-index'];
  }

  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.checkboxStates = new Map(); // Store checkbox states at the moment of click

    // ✅ MOBILE-ONLY: detect mobile the same way your theme uses for sidebar breakpoints
    this.isMobile = window.matchMedia('(max-width: 1067px)').matches;

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const form = this.querySelector('form');

    // Capture checkbox state immediately on input event (DESKTOP ONLY)
    form.addEventListener('input', (event) => {
      // ✅ MOBILE-ONLY: do NOT maintain checkboxStates on mobile.
      // This prevents "ghost" re-checking caused by async re-renders.
      if (!this.isMobile) {
        if (event.target.type === 'checkbox') {
          const key = `${event.target.name}=${event.target.value}`;
          if (event.target.checked) {
            this.checkboxStates.set(key, true);
          } else {
            this.checkboxStates.delete(key);
          }
        }
      }

      this.debouncedOnSubmit(event);
    });
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }
  set selectedIndex(index) {
    this.setAttribute('selected-index', index);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static toggleActiveTabs() {
    document
      .querySelectorAll('.facets-popup-modal--tabs-button')
      .forEach((element, i) => {
        element.classList.toggle('active', i == FacetFiltersForm.selectedIndex);
      });
    document
      .querySelectorAll('.facets-popup-modal--tab')
      .forEach((element, i) => {
        element.classList.toggle('active', i == FacetFiltersForm.selectedIndex);
      });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName('thb-filter-count');
    document
      .getElementById('ProductGridContainer')
      .querySelector('.collection')
      .classList.add('loading');

    for (var item of container) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            url,
          },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    ScrollTrigger.batch(
      '.animations-true .product-card .product-featured-image-link, .animations-true .products .gallery--item figure',
      {
        start: 'top 90%',
        onEnter: (elements, triggers) => {
          gsap.to(elements, {
            scale: 1,
            opacity: 1,
            stagger: 0.15,
            ease: window.theme.settings.animation_easing,
          });
        },
      },
    );
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetsModal-inner .facets-popup-modal--tab',
    );
    const matchesIndex = (element) => {
      const jsFilter = event
        ? event.target.closest('.facets-popup-modal--tab')
        : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element),
    );

    facetsToRender.forEach((element) => {
      document.querySelector(
        `.facets-popup-modal--tab[data-index="${element.dataset.index}"]`,
      ).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderTabs(parsedHTML);
    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML, event);
    ensureSidebarStaysOpen();

    if (typeof SelectWidth !== 'undefined') {
      new SelectWidth();
    }
  }

  static renderTabs(html) {
    const tabsToRender = html.querySelector(
      '#FacetsModal-inner .facets-popup-modal--tabs',
    );
    const buttons = Array.from(
      html.querySelectorAll('.facets-popup-modal--tabs-button'),
    );
    const index = FacetFiltersForm.selectedIndex;

    const tabSelectors = [
      '#FacetsModal-inner .facets-popup-modal--tabs, #FacetsModal-inner .facets-popup-modal__content-info',
    ];

    tabSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveTabs();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets', 'facet-active-filters'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);

      if (targetElement) {
        if (activeFacetsElement) {
          targetElement.innerHTML = activeFacetsElement.innerHTML;
        } else {
          // Si no existe el elemento en el HTML nuevo, limpiarlo
          targetElement.innerHTML = '';
        }

        // Re-setup listeners for the new elements
        if (selector === 'facet-active-filters' && targetElement.setupListeners) {
          targetElement.setupListeners();
        }
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html, event) {
    const facetButtons = [
      '#Facets-Toggle',
      '#Facets-Toggle-Fixed',
      '#FacetFiltersForm-buttons',
      '#FacetFiltersForm-sidebar',
      '.thb-filter-sort-count--bar',
      '.thb-filter-sort-count--modal',
    ];

    const isFromSidebarCheckbox =
      event && event.target && event.target.closest('#FacetFiltersForm-sidebar');

    facetButtons.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        // Don't update sidebar if the event came from sidebar checkbox
        if (selector === '#FacetFiltersForm-sidebar' && isFromSidebarCheckbox) {
          // Update only the counts within sidebar items, not the entire sidebar
          const newSidebar = html.querySelector(selector);
          const currentItems = targetElement.querySelectorAll('.facet-filter-options li');
          const newItems = newSidebar.querySelectorAll('.facet-filter-options li');

          currentItems.forEach((item, index) => {
            if (newItems[index]) {
              const currentCount = item.querySelector('.count');
              const newCount = newItems[index].querySelector('.count');
              if (currentCount && newCount) {
                currentCount.textContent = newCount.textContent;
              }
            }
          });
          return; // Skip full sidebar update
        }
        targetElement.innerHTML = html.querySelector(selector).innerHTML;
      }
    });

    ensureSidebarStaysOpen();
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');

    // ✅ MOBILE-ONLY: build params from actual form state (prevents auto re-select)
    // ✅ DESKTOP: keep original checkboxStates logic untouched.
    const searchParams = new URLSearchParams();

    if (this.isMobile) {
      const formData = new FormData(form);
      for (const [name, value] of formData.entries()) {
        if (name && value !== '') searchParams.append(name, value);
      }
    } else {
      // Build search params only from checked checkboxes and other inputs
      // Use the stored checkbox states instead of reading from DOM
      this.checkboxStates.forEach((checked, key) => {
        const [name, value] = key.split('=');
        searchParams.append(name, value);
      });

      // Add all other input types (text, hidden, etc)
      form
        .querySelectorAll('input:not([type="checkbox"]), select')
        .forEach((input) => {
          if (input.name && input.value) {
            searchParams.append(input.name, input.value);
          }
        });
    }

    if (searchParams.get('filter.v.price.gte') === '0.00') {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (
        searchParams.get('filter.v.price.lte') ===
        parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)
      ) {
        searchParams.delete('filter.v.price.lte');
      }
    }

    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    // Clear checkbox states when filters are cleared (DESKTOP ONLY usage)
    this.checkboxStates.clear();

    // Parse the URL to rebuild checkbox states based on the new URL (DESKTOP ONLY usage)
    // ✅ This remains as-is to avoid any desktop behaviour change.
    const params = new URLSearchParams(url);
    params.forEach((value, key) => {
      if (key.startsWith('filter.')) {
        const stateKey = `${key}=${value}`;
        this.checkboxStates.set(stateKey, true);
      }
    });

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

function hasActiveSidebarFilters(facetsBar) {
  if (!facetsBar) {
    return false;
  }

  const form = facetsBar.querySelector('form');
  if (!form) {
    return false;
  }

  const hasActiveCheckbox = form.querySelector(
    '.facet-filter-options input[type="checkbox"]:checked',
  );
  const hasActiveLinks = form.querySelector('.facet-filter-link--active');
  const hasActiveTags = facetsBar.querySelector('.active-filter-tag');
  const minPrice = form.querySelector('input[name="filter.v.price.gte"]');
  const maxPrice = form.querySelector('input[name="filter.v.price.lte"]');
  const hasPriceValue =
    (minPrice && minPrice.value) || (maxPrice && maxPrice.value);

  return Boolean(
    hasActiveCheckbox || hasActiveLinks || hasActiveTags || hasPriceValue,
  );
}

function ensureSidebarStaysOpen() {
  const facetsBar = document.querySelector('facet-filters-form');
  if (!facetsBar || window.innerWidth < 1068) {
    return;
  }

  if (
    facetsBar.classList.contains('filters-expanded') ||
    !hasActiveSidebarFilters(facetsBar)
  ) {
    return;
  }

  facetsBar.classList.add('filters-expanded');
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form');
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);

class FacetActiveFilters extends HTMLElement {
  constructor() {
    super();
    this.boundHandlers = new Map();
    this.setupListeners();
  }

  setupListeners() {
    // Remove old listeners first
    this.boundHandlers.forEach((handler, element) => {
      element.removeEventListener('click', handler);
    });
    this.boundHandlers.clear();

    // Add new listeners
    this.querySelectorAll('a').forEach((item) => {
      const handler = (event) => {
        event.preventDefault();
        const form = document.querySelector('facet-filters-form');
        if (form) {
          form.onActiveFilterClick(event);
        }
      };
      item.addEventListener('click', handler);
      this.boundHandlers.set(item, handler);
    });
  }
}

customElements.define('facet-active-filters', FacetActiveFilters);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector('.price_slider'),
      amounts = this.querySelector('.price_slider_amount'),
      args = {
        start: [
          parseFloat(slider.dataset.minValue || 0),
          parseFloat(slider.dataset.maxValue || slider.dataset.max),
        ],
        connect: true,
        step: 10,
        direction: document.dir,
        range: {
          min: 0,
          max: parseFloat(slider.dataset.max),
        },
      },
      event = new CustomEvent('input'),
      form =
        this.closest('facet-filters-form') ||
        document.querySelector('facet-filters-form');

    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);

    slider.noUiSlider.on('update', function (values) {
      amounts.querySelector('.field__input_min').value = values[0];
      amounts.querySelector('.field__input_max').value = values[1];
    });
    slider.noUiSlider.on('change', function (values) {
      form.querySelector('form').dispatchEvent(event);
    });
  }
}
customElements.define('price-slider', PriceSlider);

/**
 *  @class
 *  @function FacetsButtons
 */
if (!customElements.get('facet-buttons')) {
  class FacetButtons extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.setupObservers();

      this.debouncedOnDom = debounce((event) => {
        this.setupObservers();
      }, 500);

      this.addEventListener('DOMNodeInserted', this.debouncedOnDom.bind(this));
    }
    setupObservers() {
      this.buttons = this.querySelectorAll('button');
      this.drawer = document.getElementById(this.dataset.target);
      this.links = this.drawer.querySelectorAll('.facets-popup-modal--tabs-button');
      this.panels = this.drawer.querySelectorAll('.facets-popup-modal--tab');

      this.buttons.forEach((item, i) => {
        item.addEventListener('click', (e) => this.onClick(e, i));
      });
      this.links.forEach((item, i) => {
        item.addEventListener('click', (e) => this.onClick(e, i));
      });
    }
    onClick(e, i) {
      this.links.forEach((link) => {
        link.classList.remove('active');
      });
      this.links[i].classList.add('active');

      this.panels.forEach((panel) => {
        panel.classList.remove('active');
      });
      this.panels[i].classList.add('active');

      FacetFiltersForm.selectedIndex = i;
      e.preventDefault();
    }
  }

  customElements.define('facet-buttons', FacetButtons);
}

/**
 *  @class
 *  @function FacetToggle
 */
if (!customElements.get('facet-toggle')) {
  class FacetToggle extends HTMLElement {
    constructor() {
      super();
      this.handleResize = this.handleResize.bind(this);
    }
    connectedCallback() {
      this.drawer = document.getElementById(this.dataset.target);
      const collectionContainer = this.closest('.collection-container');
      this.wrapper = collectionContainer
        ? collectionContainer.querySelector('#product-grid')
        : null;
      this.form = this.closest('#FacetFiltersForm');
      this.facetsBar = this.closest('facet-filters-form');
      this.sidebarOnly = this.facetsBar
        ? this.facetsBar.classList.contains('filters-always-on')
        : false;
      this.addEventListener('click', this.onClick.bind(this));

      this.setupObservers();
      window.addEventListener('resize', this.handleResize);
    }
    disconnectedCallback() {
      if (this.observer) {
        this.observer.unobserve(this.wrapper);
      }
      window.removeEventListener('resize', this.handleResize);
    }
    setupObservers() {
      window.addEventListener('scroll', this.onScroll.bind(this));
    }
    handleResize() {
      // Close sidebar when switching to mobile
      if (window.innerWidth < 1068 && this.facetsBar) {
        this.facetsBar.classList.remove('filters-expanded');
        return;
      }

      ensureSidebarStaysOpen();
    }
    onClick() {
      if (!this.facetsBar) {
        if (this.drawer) {
          this.drawer.show();
        }
        return;
      }

      const isSidebarLayout = this.facetsBar.classList.contains('facets--sidebar');
      const shouldForceSidebarToggle = this.sidebarOnly;

      if (shouldForceSidebarToggle || (window.innerWidth >= 1068 && isSidebarLayout)) {
        this.facetsBar.classList.toggle('filters-expanded');
        return;
      }

      if (this.drawer) {
        this.drawer.show();
      }
    }
    onScroll() {
      if (!this.wrapper) {
        return;
      }
      let total =
        this.wrapper.offsetTop +
        this.wrapper.offsetHeight -
        window.innerHeight +
        90;

      if (window.scrollY < this.wrapper.offsetTop) {
        this.classList.remove('sticky');
      } else if (
        window.scrollY > this.wrapper.offsetTop &&
        window.scrollY < total
      ) {
        this.classList.add('sticky');
      } else if (window.scrollY >= total) {
        this.classList.remove('sticky');
      }
    }
  }

  customElements.define('facet-toggle', FacetToggle);
}

ensureSidebarStaysOpen();